import { XMarkIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { OverlayScrollArea } from '@odo/shared-ui';

interface EventDetailModalProps {
    readonly title: string;
    readonly body: React.ReactNode;
    readonly onClose: () => void;
}

const EventDetailModal: React.FC<EventDetailModalProps> = ({ title, body, onClose }) => {
    const dialogRef = useRef<HTMLDivElement>(null);
    const previousFocusRef = useRef<HTMLElement | null>(null);

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
                return;
            }

            if (e.key !== 'Tab' || !dialogRef.current) return;

            const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
                'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
            );
            if (focusable.length === 0) return;

            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        },
        [onClose]
    );

    useEffect(() => {
        previousFocusRef.current = document.activeElement as HTMLElement;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        dialogRef.current?.focus();
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleKeyDown);
            previousFocusRef.current?.focus();
        };
    }, [handleKeyDown]);

    const modalContent = (
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex justify-center items-center"
            onClick={onClose}
        >
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-label={title}
                tabIndex={-1}
                className="bg-white rounded-xl shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col overflow-hidden outline-none"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
                    <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
                    <button
                        onClick={onClose}
                        aria-label="Close dialog"
                        className="p-2 -m-2 text-gray-400 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Body: full design freedom via React node. The scroll region is a
                    flex-1/min-h-0 item so the panel hugs short content and only
                    scrolls once total height hits the max-h-[90vh] cap above.
                    The explicit max-h is load-bearing, not belt-and-braces:
                    OverlayScrollArea sizes its scroll region with h-full, and a
                    percentage height resolves against an ancestor whose own height
                    is only *capped* (max-h-[90vh]), not definite - so it collapses
                    to auto, the region grows to fit its content, overflow-y-auto
                    never engages, and the panel's overflow-hidden clips the body
                    instead of scrolling it. The calc subtracts the header and
                    footer, which are flex siblings outside this region. */}
                <OverlayScrollArea
                    className="flex-1 min-h-0"
                    contentClassName="pl-6 pr-8 py-6 max-h-[calc(90vh-9rem)]"
                    trackInsetPx={8}
                >
                    {body}
                </OverlayScrollArea>

                {/* Footer */}
                <div className="flex items-center justify-end px-6 py-4 border-t border-gray-100">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-semibold border border-gray-300 rounded-lg bg-white hover:bg-gray-50 text-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );

    const modalRoot = document.getElementById('modal-root');
    return modalRoot ? ReactDOM.createPortal(modalContent, modalRoot) : null;
};

export default EventDetailModal;
